import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { resetPassword } from '../services/api';
import './ResetPassword.css';

const ResetPassword = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');

    if (newPassword.length < 6) {
      setError(t('auth.resetPassword.tooShort', 'Password must be at least 6 characters'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('auth.resetPassword.mismatch', 'Passwords do not match'));
      return;
    }
    if (!token.trim()) {
      setError(t('auth.resetPassword.invalidLink', 'Invalid reset link. Please request a new one from the forgot password page.'));
      return;
    }

    setLoading(true);
    try {
      await resetPassword(token, newPassword);
      setMessage(t('auth.resetPassword.successMessage', 'Password has been reset. You can now log in with your new password.'));
      setNewPassword('');
      setConfirmPassword('');
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      const msg = err.response?.data?.error || t('auth.resetPassword.failed', 'Failed to reset password. The link may have expired.');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!token.trim()) {
    return (
      <div className="reset-password-page fade-in">
        <div className="reset-password-container">
          <div className="reset-password-header">
            <h1>{t('auth.resetPassword.title', 'Reset Password')}</h1>
            <p className="error-message">
              {t('auth.resetPassword.invalidLink', 'Invalid reset link. Please request a new one from the forgot password page.')}
            </p>
          </div>
          <div className="back-to-login">
            <Link to="/forgot-password">{t('auth.resetPassword.requestNew', 'Request new link')}</Link>
            <span style={{ margin: '0 0.5rem' }}>|</span>
            <Link to="/login">{t('auth.forgotPassword.backToLogin', 'Back to login')}</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="reset-password-page fade-in">
      <div className="reset-password-container">
        <div className="reset-password-header">
          <div className="reset-password-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0110 0v4"/>
            </svg>
          </div>
          <h1>{t('auth.resetPassword.title', 'Set new password')}</h1>
          <p>{t('auth.resetPassword.subtitle', 'Enter your new password below.')}</p>
        </div>

        {error && (
          <div className="error-message" style={{ marginBottom: 'var(--space-6)' }}>
            {error}
          </div>
        )}
        {message && (
          <div className="success-message" style={{ marginBottom: 'var(--space-6)' }}>
            {message}
            <p style={{ marginTop: 'var(--space-2)', fontSize: 'var(--text-sm)' }}>
              {t('auth.resetPassword.redirecting', 'Redirecting to login...')}
            </p>
          </div>
        )}

        {!message && (
          <form onSubmit={handleSubmit} className="reset-password-form">
            <div className="form-group">
              <label htmlFor="newPassword">{t('auth.resetPassword.newPasswordLabel', 'New password')}</label>
              <div className="input-wrapper">
                <input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  className="form-input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={t('auth.resetPassword.newPasswordPlaceholder', 'At least 6 characters')}
                  required
                  minLength={6}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  tabIndex={-1}
                >
                  {showPassword ? '🙈' : '👁'}
                </button>
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="confirmPassword">{t('auth.resetPassword.confirmLabel', 'Confirm password')}</label>
              <input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                className="form-input"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder={t('auth.resetPassword.confirmPlaceholder', 'Confirm new password')}
                required
                minLength={6}
                autoComplete="new-password"
              />
            </div>

            <button
              type="submit"
              className="btn btn-primary btn-lg w-full"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="loading loading-sm"></span>
                  {t('auth.resetPassword.submitting', 'Resetting...')}
                </>
              ) : (
                t('auth.resetPassword.submitButton', 'Reset password')
              )}
            </button>
          </form>
        )}

        <div className="back-to-login">
          <Link to="/login">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="19" y1="12" x2="5" y2="12"/>
              <polyline points="12 19 5 12 12 5"/>
            </svg>
            {t('auth.forgotPassword.backToLogin', 'Back to login')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
